/*
 * Copyright (c) 2024 metromorph softworks GmbH
 *
 * All rights reserved
 */

import * as React from "react";

import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";

import AppWaitNotice from "../AppWaitNotice";
import AppMessage from "../AppMessage";
import ErrorBoundary from "../ErrorBoundary";
import FallbackScreen from "../FallbackScreen";
import ErrorScreen from "../ErrorScreen";

import { StaticLoadingScreen } from "../LoadingScreen";

import {
    AppState,
    ScreenRegistry,
    LoadingScreen,
    initialAppState
} from "@ef/feature-app";

import TimeoutMessage from "@ef/components-login/TimeoutMessage";

interface BaseAppProps {
    readonly appstate: AppState,
    //appconfig: AppConfig,
    readonly resetError: () => void
}

export default function BaseAppComponent(
    { 
        appstate: { 
            notice, loading, progress, screen = LoadingScreen, error, initialized
        } = initialAppState, 
        //appconfig: { home },
        resetError
    }: BaseAppProps
) {
    const showFooter = loading || notice || progress;

    const ScreenComponent = React.useMemo(() => {
        if (error) {
            /* eslint-disable react/display-name */
            return () => (
                <ErrorScreen
                    error={error}
                    resetError={initialized ? resetError : undefined}
                />
            );
            /* eslint-enable react/display-name */
        } else {
            //console.log("Try to render Screen", screen.name);
            const ScreenComponent = ScreenRegistry.get(screen.name);
            if (ScreenComponent) {
                return ScreenComponent;
            } else {
                /* eslint-disable react/display-name */
                return () => (<FallbackScreen />);
                /* eslint-enable react/display-name */
            }
        }
    }, [screen.name, error, resetError, initialized]);
    return (
        <React.Suspense fallback={<StaticLoadingScreen text="Loading localization…" />}>
            <ErrorBoundary>
                <Box>
                    <ScreenComponent />
                    {showFooter ? (
                        <Box
                            sx={{
                                position: "fixed",
                                bottom: 0, 
                                left: 0, 
                                right: 0
                            }}>
                            {
                                progress ? (
                                    <LinearProgress 
                                        variant="determinate"
                                        value={Math.ceil(progress.current * 100 / progress.max)}
                                    />
                                ) : (
                                    <LinearProgress /> 
                                )
                            }
                            {notice ? <AppWaitNotice notice={notice} /> : null}
                        </Box>
                    ) : null}
                </Box>
                <AppMessage />
                <TimeoutMessage />
            </ErrorBoundary>
        </React.Suspense>
    );
}
